import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ChevronRight, Search, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { PageHeader, BrandButton } from "@/components/PageHeader";
import { Pager, usePaged } from "@/components/Pager";
import { loadTemplate, downloadStickerImage, stickerData } from "@/components/labels/Sticker";
import { api } from "@/lib/api";

const EventRow = ({ ev, onOpen }) => (
  <button onClick={onOpen} className="w-full text-left bg-white border border-[#E5E7EB] rounded-lg p-4 flex items-center gap-4 hover:border-[#E60000] transition-colors" data-testid={`sd-event-${ev.id}`}>
    <div className="flex-1 min-w-0">
      <div className="font-heading font-bold truncate">{ev.title}{ev.archived && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider bg-slate-200 text-slate-700 rounded px-1.5 py-0.5">Archived</span>}</div>
      <div className="text-xs text-gray-500">{[ev.date, ev.venue].filter(Boolean).join(" · ")}</div>
    </div>
    <div className="text-center text-xs"><div className="font-heading text-lg font-bold" data-testid={`sd-event-${ev.id}-count`}>{ev.participants.length}</div><div className="text-gray-500">Participants</div></div>
    <ChevronRight className="w-4 h-4 text-gray-400" />
  </button>
);

const Row = ({ r, busy, onDownload }) => (
  <tr className="hover:bg-[#F8F9FA]" data-testid={`sd-row-${r.ticket_no}`}>
    <td className="px-3 py-2.5 font-mono text-xs font-semibold text-[#E60000] whitespace-nowrap">{r.ticket_no}</td>
    <td className="px-3 py-2.5"><div className="font-medium">{r.delegate_name}</div><div className="sm:hidden text-xs text-gray-500">{r.category_name}</div></td>
    <td className="hidden sm:table-cell px-3 py-2.5 text-xs">{r.category_name}</td>
    <td className="px-3 py-2.5 text-right">
      <BrandButton variant="outline" onClick={onDownload} disabled={busy} data-testid={`sd-download-${r.ticket_no}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}Sticker
      </BrandButton>
    </td>
  </tr>
);

const EventView = ({ ev }) => {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);
  const rows = useMemo(() => ev.participants.filter((r) => `${r.delegate_name} ${r.ticket_no}`.toLowerCase().includes(q.toLowerCase())), [ev, q]);
  const pg = usePaged(rows, q);
  const download = async (r) => {
    setBusy(r.id);
    try { const out = await downloadStickerImage(await loadTemplate(), stickerData(r, ev.title)); toast.success(`Downloaded ${out.name}`); }
    catch { toast.error("Could not create the sticker picture."); }
    finally { setBusy(null); }
  };
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
      <div className="p-3 border-b"><div className="relative max-w-md"><Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or ticket number" className="pl-9 h-10" data-testid="sd-search" /></div></div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="sd-table">
          <thead className="bg-[#F8F9FA] text-[11px] uppercase tracking-wider text-gray-500">
            <tr>{["Ticket No", "Name", "Category", ""].map((h, i) => <th key={i} className={`text-left font-bold px-3 py-2.5 whitespace-nowrap ${i === 2 ? "hidden sm:table-cell" : ""}`}>{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y">{pg.items.map((r) => <Row key={r.id} r={r} busy={busy === r.id} onDownload={() => download(r)} />)}</tbody>
        </table>
      </div>
      {!rows.length && <div className="p-10 text-center text-sm text-gray-500" data-testid="sd-empty">{ev.participants.length ? "No participants match your search." : "No submitted registrations for this event yet."}</div>}
      <Pager {...pg} testId="sd-pager" />
    </div>
  );
};

export default function StickerDesk() {
  const [events, setEvents] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    api.get("/stickers/events").then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, []);
  const current = openId && events?.find((e) => e.id === openId);
  const list = (events || []).filter((e) => e.title.toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="sticker-desk-page">
      <PageHeader eyebrow="Sticker Desk" title={current ? current.title : "Sticker Desk"} description={current ? "Find a participant and download their 40 × 80 mm sticker picture for BarTender." : "Pick an event to download participant stickers."}
        actions={current && <BrandButton variant="outline" onClick={() => setOpenId(null)} data-testid="sd-back"><ArrowLeft className="w-4 h-4" />All events</BrandButton>} />
      {!events ? <div className="text-gray-500">Loading…</div> : current ? <EventView key={current.id} ev={current} /> : <>
        <div className="relative mb-4 max-w-md"><Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find an event" className="pl-9 h-10 bg-white" data-testid="sd-event-search" /></div>
        <div className="space-y-3">
          {list.map((ev) => <EventRow key={ev.id} ev={ev} onOpen={() => setOpenId(ev.id)} />)}
          {!list.length && <div className="p-10 text-center text-sm text-gray-500 bg-white border rounded-lg" data-testid="sd-no-events">No events found.</div>}
        </div>
      </>}
    </div>
  );
}
